import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useQuery } from '@tanstack/react-query';
import type { CampaignCheck } from '@internal/backstage-plugin-bidone-backend';
import {
  CheckInput,
  useAddCampaignCheck,
  useCreateCampaign,
  useCreateFilter,
  useFreezeMembership,
  useUpdateCampaign,
} from '../../hooks/useCampaigns';
import { FilterBuilder, FilterDraft } from './FilterBuilder';
import { CheckEditorDialog } from './CheckEditorDialog';

export type OwnerOption = { ref: string; label: string };

export const useOwnerOptions = () => {
  const catalogApi = useApi(catalogApiRef);
  return useQuery({
    queryKey: ['campaignOwnerOptions'],
    queryFn: async (): Promise<OwnerOption[]> => {
      const { items } = await catalogApi.getEntities({
        filter: { kind: 'Group' },
      });
      return items
        .map(entity => ({
          ref: stringifyEntityRef(entity),
          label:
            (entity.spec as any)?.profile?.displayName ??
            entity.metadata.title ??
            entity.metadata.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    staleTime: 15 * 60 * 1000,
  });
};

type StagedCheck = CheckInput & { _key: number };
let _nextCheckKey = 0;

type CheckEditorState = { idx: number | null };

type CreateCampaignDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const CreateCampaignDialog = ({
  open,
  onClose,
}: CreateCampaignDialogProps) => {
  const navigate = useNavigate();

  // Basics
  const [campaignName, setCampaignName] = useState('');
  const [owner, setOwner] = useState<OwnerOption | null>(null);
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [brief, setBrief] = useState('');

  // Filter
  const [filterName, setFilterName] = useState('');
  const [filterDraft, setFilterDraft] = useState<FilterDraft>({
    combine: 'all',
    predicates: [],
  });
  const [snapshotMode, setSnapshotMode] = useState(false);
  const snapshotRefsRef = useRef<string[]>([]);
  const [snapshotCount, setSnapshotCount] = useState(0);

  // Checks
  const [pendingChecks, setPendingChecks] = useState<StagedCheck[]>([]);
  const [checkEditorOpen, setCheckEditorOpen] = useState(false);
  const [checkEditorState, setCheckEditorState] = useState<CheckEditorState>({
    idx: null,
  });

  const { data: ownerOptions } = useOwnerOptions();
  const createCampaign = useCreateCampaign();
  const createFilter = useCreateFilter();
  const updateCampaign = useUpdateCampaign();
  const addCheck = useAddCampaignCheck();
  const freeze = useFreezeMembership();

  const saving =
    createCampaign.isPending ||
    createFilter.isPending ||
    updateCampaign.isPending ||
    addCheck.isPending ||
    freeze.isPending;

  const saveError =
    createCampaign.error ??
    createFilter.error ??
    updateCampaign.error ??
    addCheck.error ??
    freeze.error;

  useEffect(() => {
    if (open) {
      setCampaignName('');
      setOwner(null);
      setStartDate('');
      setTargetDate('');
      setBrief('');
      setFilterName('');
      setFilterDraft({ combine: 'all', predicates: [] });
      setSnapshotMode(false);
      snapshotRefsRef.current = [];
      setSnapshotCount(0);
      setPendingChecks([]);
      setCheckEditorOpen(false);
      setCheckEditorState({ idx: null });
      createCampaign.reset();
      createFilter.reset();
      updateCampaign.reset();
      addCheck.reset();
      freeze.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCreate = async () => {
    // 1. Create filter if predicates are defined
    let filterId: string | null = null;
    if (filterDraft.predicates.length > 0) {
      const name =
        filterName.trim() || `${campaignName.trim()} filter`;
      const saved = await createFilter.mutateAsync({
        name,
        combine: filterDraft.combine,
        predicates: filterDraft.predicates,
      });
      filterId = saved.id;
    }

    // 2. Create the campaign shell
    const campaign = await createCampaign.mutateAsync({
      name: campaignName.trim(),
      ...(owner ? { ownerEntityRef: owner.ref } : {}),
    });

    // 3. Patch remaining fields
    const patch: Record<string, unknown> = {};
    if (startDate) patch.startDate = startDate;
    if (targetDate) patch.targetDate = targetDate;
    if (brief.trim()) patch.brief = brief.trim();
    if (filterId) patch.filterId = filterId;
    if (snapshotMode && snapshotRefsRef.current.length > 0)
      patch.membershipMode = 'snapshot';
    if (Object.keys(patch).length > 0) {
      await updateCampaign.mutateAsync({ id: campaign.id, patch });
    }

    // 4. Create staged checks
    for (const { _key: _k, ...input } of pendingChecks) {
      await addCheck.mutateAsync({ campaignId: campaign.id, input });
    }

    // 5. Freeze snapshot membership
    if (snapshotMode && snapshotRefsRef.current.length > 0) {
      await freeze.mutateAsync({
        campaignId: campaign.id,
        refs: snapshotRefsRef.current,
      });
    }

    onClose();
    navigate(`/campaigns/${encodeURIComponent(campaign.id)}`);
  };

  // Build a synthetic CampaignCheck for the editor when editing a staged check
  const editingCheck =
    checkEditorState.idx !== null
      ? (() => {
          const { _key: _k, ...rest } = pendingChecks[checkEditorState.idx];
          return { id: '', campaignId: '', ...rest };
        })()
      : null;

  const openAddCheck = () => {
    setCheckEditorState({ idx: null });
    setCheckEditorOpen(true);
  };

  const openEditCheck = (idx: number) => {
    setCheckEditorState({ idx });
    setCheckEditorOpen(true);
  };

  const handleLocalCheck = (input: CheckInput) => {
    const { idx } = checkEditorState;
    if (idx !== null) {
      setPendingChecks(prev =>
        prev.map((c, i) =>
          i === idx ? { ...input, _key: c._key } : c,
        ),
      );
    } else {
      setPendingChecks(prev => [...prev, { ...input, _key: _nextCheckKey++ }]);
    }
  };

  const removeCheck = (idx: number) => {
    setPendingChecks(prev => prev.filter((_c, i) => i !== idx));
  };

  const hasFilter = filterDraft.predicates.length > 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>New campaign</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
        >
          {saveError ? (
            <Alert severity="error">{(saveError as Error).message}</Alert>
          ) : null}

          {/* ── Basics ── */}
          <Typography variant="subtitle1" fontWeight={600}>
            Campaign details
          </Typography>
          <TextField
            label="Name"
            required
            variant="outlined"
            size="small"
            fullWidth
            value={campaignName}
            onChange={e => setCampaignName(e.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Autocomplete
              sx={{ flex: 2, minWidth: 200 }}
              size="small"
              options={ownerOptions ?? []}
              getOptionLabel={o => o.label}
              isOptionEqualToValue={(a, b) => a.ref === b.ref}
              value={owner}
              onChange={(_e, next) => setOwner(next)}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Owner (defaults to you)"
                  variant="outlined"
                />
              )}
            />
            <TextField
              sx={{ flex: 1, minWidth: 140 }}
              label="Start date"
              type="date"
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
            <TextField
              sx={{ flex: 1, minWidth: 140 }}
              label="Target date"
              type="date"
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={targetDate}
              onChange={e => setTargetDate(e.target.value)}
            />
          </Box>
          <TextField
            label="Project brief"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={3}
            placeholder="What is this campaign about? Why now? What does done look like?"
            value={brief}
            onChange={e => setBrief(e.target.value)}
          />

          <Divider />

          {/* ── Filter ── */}
          <Typography variant="subtitle1" fontWeight={600}>
            Targeting filter
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Optional — leave empty to apply to all components.
          </Typography>
          {hasFilter && (
            <TextField
              label="Filter name"
              variant="outlined"
              size="small"
              fullWidth
              helperText={`Auto-named "${campaignName.trim() || '…'} filter" if left blank`}
              value={filterName}
              onChange={e => setFilterName(e.target.value)}
            />
          )}
          <FilterBuilder
            value={filterDraft}
            onChange={setFilterDraft}
            snapshotMode={snapshotMode}
            onSnapshotSelectionChange={refs => {
              snapshotRefsRef.current = refs;
              setSnapshotCount(refs.length);
            }}
          />
          {hasFilter && (
            <FormControlLabel
              control={
                <Switch
                  checked={snapshotMode}
                  onChange={e => setSnapshotMode(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Freeze membership as snapshot
                  {snapshotMode && snapshotCount > 0
                    ? ` — ${snapshotCount} component${snapshotCount === 1 ? '' : 's'} selected`
                    : ''}
                </Typography>
              }
            />
          )}

          <Divider />

          {/* ── Checks ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Checks ({pendingChecks.length})
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={openAddCheck}>
              Add check
            </Button>
          </Box>
          {pendingChecks.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No checks yet — you can also add them after creating.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingChecks.map((c, idx) => (
                  <TableRow key={c._key}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>
                      {c.kind === 'manual' ? 'Manual' : 'Automated'}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {c.description || '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton
                        size="small"
                        aria-label="Edit check"
                        onClick={() => openEditCheck(idx)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Remove check"
                        onClick={() => removeCheck(idx)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={
              !campaignName.trim() ||
              saving ||
              (snapshotMode && snapshotCount === 0 && hasFilter)
            }
            onClick={() => {
              handleCreate().catch(() => undefined);
            }}
          >
            {saving ? 'Creating…' : 'Create campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      <CheckEditorDialog
        open={checkEditorOpen}
        check={editingCheck as CampaignCheck | null}
        onAddLocal={handleLocalCheck}
        onClose={() => setCheckEditorOpen(false)}
      />
    </>
  );
};
